import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Card,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  AlertTitle,
  CircularProgress,
  Tooltip,
  Divider,
  Snackbar,
  List,
  Paper as MuiPaper,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  DoubleArrow as CheckInIcon,
  ArrowBack as CheckOutIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  DirectionsBoat as BoatIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { format, isPast, isToday, parseISO, isFuture, addDays, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getAllReservations, 
  getAllReservationsWithCustomers,
  updateReservation, 
  getReservationById 
} from '../../firebase/services/reservationService';
import { processRefund } from '../../firebase/services/squareService';
import { formatPhoneNumber, formatTime12Hour } from '../../utils/formatters';
import { sendEmail, initializeDefaultTemplates } from '../../firebase/services/emailService';
import { getTemplates } from '../../firebase/services/transactionalEmailTemplateService';
import { useLocationContext } from '../../contexts/LocationContext';
import { collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { formatInTimeZone } from 'date-fns-tz';
import { getEmailCorrespondenceForReservation } from '../../firebase/services/emailCorrespondenceService';
import { EmailCorrespondence } from '../../models/types';
import CheckInDialog from './CheckInDialog';

// Define interfaces for tab panels
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reservation-tabpanel-${index}`}
      aria-labelledby={`reservation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `reservation-tab-${index}`,
    'aria-controls': `reservation-tabpanel-${index}`,
  };
}

// Add helper for title case
function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

// Helper function to convert time string (e.g., "8:00 AM") to minutes since midnight for proper sorting
function convertTimeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  
  try {
    // Handle formats like "8:00 AM", "11:00 AM", "1:00 PM", etc.
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let hour24 = hours;
    if (period === 'PM' && hours !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hours === 12) {
      hour24 = 0;
    }
    
    return hour24 * 60 + (minutes || 0);
  } catch (error) {
    console.warn('Error parsing time string:', timeStr, error);
    return 0;
  }
}

// Sortable table cell component
interface SortableTableCellProps {
  children: React.ReactNode;
  sortKey: string;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
  sx?: any;
}

function SortableTableCell({ children, sortKey, sortColumn, sortDirection, onSort, sx }: SortableTableCellProps) {
  const isActive = sortColumn === sortKey;
  
  return (
    <TableCell
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        },
        ...sx
      }}
      onClick={() => onSort(sortKey)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {children}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ArrowUpwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          )
        ) : (
          <ArrowUpwardIcon sx={{ fontSize: 16, color: 'transparent' }} />
        )}
      </Box>
    </TableCell>
  );
}

const StaffReservationsPage: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { location } = useLocationContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // State for reservations data
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for filtering and pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // State for sorting
  const [sortColumn, setSortColumn] = useState<string>('dateTime'); // Default sort by date+time
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc'); // Default ascending
  
  // State for dialogs
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [openCheckInDialog, setOpenCheckInDialog] = useState(false);
  const [openCheckOutDialog, setOpenCheckOutDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [checkInNotes, setCheckInNotes] = useState('');
  const [checkOutNotes, setCheckOutNotes] = useState('');
  const [showRefundOption, setShowRefundOption] = useState(false);
  
  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [isCheckInMode, setIsCheckInMode] = useState(false);
  
  // State for detailed view
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailReservation, setDetailReservation] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTabValue, setDetailTabValue] = useState(0);
  const [correspondence, setCorrespondence] = useState<EmailCorrespondence[]>([]);
  const [correspondenceLoading, setCorrespondenceLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  
  // New state for resend dialog
  const [openResendDialog, setOpenResendDialog] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  
  // New state for templates
  const [templates, setTemplates] = useState<any[]>([]);
  
  // State for correspondence detailed view
  const [emailDetailDialogOpen, setEmailDetailDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailCorrespondence | null>(null);
  
  // Hardcoded location ID for Fallen Leaf Marina
  const FALLEN_LEAF_LOCATION_ID = 'mz9knr7tZGItuksXGvjM';
  
  // New state for stat card filter
  const [statCardFilter, setStatCardFilter] = useState<string | null>(null);
  
  const resultsTableRef = useRef<HTMLDivElement>(null);
  
  // New state for check-in dialog
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkInReservation, setCheckInReservation] = useState<any>(null);
  
  // Fetch reservations
  useEffect(() => {
    const fetchReservations = async () => {
      try {
        setLoading(true);
        const data = await getAllReservationsWithCustomers();
        setReservations(data);
      } catch (err) {
        console.error('Error fetching reservations:', err);
        setError('Failed to load reservations. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReservations();
  }, []);
  
  // Fetch templates when location changes
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        // Get templates from the location's transactionalEmailTemplates subcollection
        const templatesRef = collection(db, `locations/${FALLEN_LEAF_LOCATION_ID}/transactionalEmailTemplates`);
        console.log('Template collection path:', `locations/${FALLEN_LEAF_LOCATION_ID}/transactionalEmailTemplates`);
        const templatesSnapshot = await getDocs(templatesRef);
        console.log('Template snapshot size:', templatesSnapshot.size);
        const templatesData = templatesSnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Template data:', { id: doc.id, ...data });
          return {
            id: doc.id,
            ...data
          };
        });
        console.log('All fetched templates:', templatesData);
        setTemplates(templatesData);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
    fetchTemplates();
  }, []); // Remove location?.id dependency since we're using hardcoded ID
  
  // Handlers for tabs
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handlers for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handlers for filtering
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };
  
  const handleDateFilterChange = (newDate: Date | null) => {
    setDateFilter(newDate);
    setPage(0);
  };
  
  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter(null);
    setSortColumn('dateTime');
    setSortDirection('asc');
    setPage(0);
  };
  
  // Handler for sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Same column clicked, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Different column clicked, set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting changes
  };
  
  // Function to handle tab change in details dialog
  const handleDetailTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDetailTabValue(newValue);
    
    // Load correspondence data when switching to correspondence tab
    if (newValue === 1 && detailReservation && correspondence.length === 0) {
      fetchCorrespondenceData(detailReservation.id);
    }
  };

  // Function to fetch correspondence data
  const fetchCorrespondenceData = async (reservationId: string) => {
    try {
      setCorrespondenceLoading(true);
      const correspondenceData = await getEmailCorrespondenceForReservation(reservationId);
      setCorrespondence(correspondenceData);
    } catch (error) {
      console.error('Error fetching correspondence data:', error);
      setSnackbarMessage('Failed to load email correspondence');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setCorrespondenceLoading(false);
    }
  };
  
  // Function to show reservation details
  const handleShowDetails = async (reservationId: string) => {
    setDetailLoading(true);
    setDetailDialogOpen(true);
    setDetailTabValue(0); // Reset to first tab
    setCorrespondence([]); // Reset correspondence data
    
    try {
      const reservation = await getReservationById(reservationId);
      if (reservation) {
        setDetailReservation(reservation);
        // Pre-fetch correspondence data
        fetchCorrespondenceData(reservationId);
      } else {
        setSnackbarMessage('Reservation not found');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching reservation details:', error);
      setSnackbarMessage('Failed to load reservation details');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDetailLoading(false);
    }
  };
  
  // Dialog handlers
  const handleCancelReservation = (reservation: any) => {
    setSelectedReservation(reservation);
    setCancelReason('');
    setCancelReasonError('');
    
    // Check if this reservation has a payment that might need refunding
    const hasPayment = reservation.payment && 
                      reservation.payment.id && 
                      !reservation.payment.refunded;
    
    setShowRefundOption(hasPayment);
    setOpenCancelDialog(true);
  };
  
  const handleConfirmCancel = async () => {
    if (!selectedReservation) return;
    
    // Validate reason is provided
    if (!cancelReason.trim()) {
      setCancelReasonError('Please enter a reason for cancellation');
      return;
    }
    
    try {
      setCancelReasonError('');
      
      // Update reservation status to cancelled with reason
      await updateReservation(selectedReservation.id, { 
        status: 'cancelled',
        cancelReason: cancelReason,
        cancelledBy: userProfile?.id || 'staff',
        cancelledAt: new Date().toISOString()
      }, userProfile?.id, 'staff', `Reservation cancelled: ${cancelReason}`, userProfile);
      
      // Update local state
      setReservations(prevReservations => 
        prevReservations.map(res => 
          res.id === selectedReservation.id 
            ? { ...res, 
                status: 'cancelled', 
                cancelReason,
                cancelledAt: new Date().toISOString(),
                cancelledBy: userProfile?.id || 'staff'
              } 
            : res
        )
      );
      
      setSnackbarMessage('Reservation cancelled successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // If there's a payment and refund is needed, prompt for refund
      if (showRefundOption) {
        // Close the cancel dialog
        setOpenCancelDialog(false);
        
        // Pre-populate refund information
        if (selectedReservation.payment) {
          setRefundAmount(selectedReservation.payment.amount.toString());
          setRefundReason(`Refund for cancellation: ${cancelReason}`);
          
          // Show refund dialog
          setTimeout(() => {
            setOpenRefundDialog(true);
          }, 500);
        }
      } else {
        // Just close the dialog if no refund needed
        setOpenCancelDialog(false);
      }
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      setSnackbarMessage('Failed to cancel reservation');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      if (!showRefundOption) {
        setCancelReason('');
        setSelectedReservation(null);
      }
    }
  };
  
  const handleRefundReservation = (reservation: any) => {
    setSelectedReservation(reservation);
    setRefundAmount(reservation.finalPrice.toString());
    setOpenRefundDialog(true);
  };
  
  const handleConfirmRefund = async () => {
    if (!selectedReservation || !selectedReservation.payment) return;
    
    try {
      setLoading(true);
      
      // Process the refund through Square API
      const refundResult = await processRefund(
        selectedReservation.payment.id,
        parseFloat(refundAmount),
        refundReason
      );
      
      if (!refundResult.success) {
        throw new Error(refundResult.error || 'Failed to process refund');
      }
      
      // Create an updated payment object
      const updatedPayment = {
        ...selectedReservation.payment,
        refunded: true,
        refundAmount: parseFloat(refundAmount),
        refundDate: new Date().toISOString(),
        refundReason: refundReason,
        refundedBy: userProfile?.id || 'staff',
        refundId: refundResult.refundId || null
      };
      
      // Create a refund entry for the payment history
      const refundEntry = {
        id: refundResult.refundId || `refund-${Date.now()}`,
        amount: -parseFloat(refundAmount), // Negative amount for refunds
        totalAmount: selectedReservation.totalPrice || selectedReservation.finalPrice || 0,
        processor: selectedReservation.payment.processor || 'square',
        paymentDate: new Date().toISOString(),
        refunded: false, // This is already a refund
        status: refundResult.status || 'paid',
        paymentDetails: {
          paymentMethod: 'refund',
          cardBrand: selectedReservation.payment.paymentDetails?.cardBrand || null,
          last4: selectedReservation.payment.paymentDetails?.last4 || null
        }
      };
      
      // Get existing payment history or create a new array with just the original payment
      const existingHistory = selectedReservation.paymentHistory || 
        [{ ...selectedReservation.payment, paymentDate: selectedReservation.payment.paymentDate || selectedReservation.createdAt }];
      
      // Add the refund to the payment history
      const updatedHistory = [...existingHistory, refundEntry];
      
      // Clean the data to remove any undefined values
      const updateData = JSON.parse(JSON.stringify({
        paymentStatus: 'refunded',
        payment: updatedPayment,
        paymentHistory: updatedHistory
      }));
      
      // Update the reservation with refund information
      await updateReservation(selectedReservation.id, updateData);
      
      // Update local state
      setReservations(prevReservations => 
        prevReservations.map(res => 
          res.id === selectedReservation.id 
            ? { 
                ...res, 
                paymentStatus: 'refunded',
                payment: updatedPayment,
                paymentHistory: updatedHistory
              } 
            : res
        )
      );
      
      setSnackbarMessage('Payment refunded successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error refunding payment:', err);
      setSnackbarMessage(`Failed to process refund: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setOpenRefundDialog(false);
      setRefundAmount('');
      setRefundReason('');
      setSelectedReservation(null);
    }
  };
  
  const handleCheckIn = (reservation: any) => {
    setSelectedReservation(reservation);
    setOpenCheckInDialog(true);
  };
  
  const handleConfirmCheckIn = async () => {
    if (!selectedReservation) return;
    
    try {
      await updateReservation(selectedReservation.id, { 
        checkInStatus: 'checked-in',
        checkInTime: new Date().toISOString(),
        checkInNotes: checkInNotes,
        checkInBy: userProfile?.id || 'staff'
      }, userProfile?.id, 'staff', `Customer checked in: ${checkInNotes}`, userProfile);
      
      // Update local state
      setReservations(prevReservations => 
        prevReservations.map(res => 
          res.id === selectedReservation.id 
            ? { 
                ...res, 
                checkInStatus: 'checked-in',
                checkInTime: new Date().toISOString(),
                checkInNotes: checkInNotes
              } 
            : res
        )
      );
      
      setSnackbarMessage('Customer checked in successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error checking in customer:', err);
      setSnackbarMessage('Failed to check in customer');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setOpenCheckInDialog(false);
      setCheckInNotes('');
      setSelectedReservation(null);
    }
  };
  
  const handleCheckOut = (reservation: any) => {
    setSelectedReservation(reservation);
    setOpenCheckOutDialog(true);
  };
  
  const handleConfirmCheckOut = async () => {
    if (!selectedReservation) return;
    
    try {
      await updateReservation(selectedReservation.id, { 
        checkInStatus: 'checked-out',
        checkOutTime: new Date().toISOString(),
        checkOutNotes: checkOutNotes,
        checkOutBy: userProfile?.id || 'staff'
      }, userProfile?.id, 'staff', `Customer checked out: ${checkOutNotes}`, userProfile);
      
      // Update local state
      setReservations(prevReservations => 
        prevReservations.map(res => 
          res.id === selectedReservation.id 
            ? { 
                ...res, 
                checkInStatus: 'checked-out',
                checkOutTime: new Date().toISOString(),
                checkOutNotes: checkOutNotes
              } 
            : res
        )
      );
      
      setSnackbarMessage('Customer checked out successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error checking out customer:', err);
      setSnackbarMessage('Failed to check out customer');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setOpenCheckOutDialog(false);
      setCheckOutNotes('');
      setSelectedReservation(null);
    }
  };
  
  // Filter reservations based on criteria and tab
  const getFilteredReservations = () => {
    let filtered = [...reservations];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (res) => 
          res.customerDetails.firstName.toLowerCase().includes(searchLower) ||
          res.customerDetails.lastName.toLowerCase().includes(searchLower) ||
          res.customerDetails.email.toLowerCase().includes(searchLower) ||
          res.vesselName.toLowerCase().includes(searchLower) ||
          res.id.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(res => res.status === statusFilter);
    }
    
    // Apply date filter
    if (dateFilter) {
      const filterDate = format(dateFilter, 'yyyy-MM-dd');
      filtered = filtered.filter(res => res.date === filterDate);
    }
    
    // Apply stat card filter FIRST (takes precedence over tab filters)
    if (statCardFilter === 'today') {
      const today = format(new Date(), 'yyyy-MM-dd');
      filtered = filtered.filter(res => res.date === today);
    } else if (statCardFilter === 'week') {
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
      filtered = filtered.filter(res => {
        const reservationDate = parseISO(res.date);
        return isWithinInterval(reservationDate, { start: weekStart, end: weekEnd });
      });
    } else if (statCardFilter === 'completed') {
      filtered = filtered.filter(res => res.checkInStatus === 'checked-out');
    } else if (statCardFilter === 'cancelled') {
      filtered = filtered.filter(res => res.status === 'cancelled');
    } else {
      // Only apply tab-specific filters if no stat card filter is active
      switch (tabValue) {
        case 0: // Today's reservations
          const today = format(new Date(), 'yyyy-MM-dd');
          filtered = filtered.filter(res => 
            res.date === today && 
            res.status !== 'cancelled' && 
            res.status !== 'completed' && 
            res.checkInStatus !== 'checked-out'
          );
          break;
        case 1: // Upcoming reservations
          filtered = filtered.filter(res => {
            const reservationDate = parseISO(res.date);
            return (isFuture(reservationDate) || isToday(reservationDate)) && 
                   res.status !== 'cancelled'; // Exclude cancelled reservations
          });
          break;
        case 2: // Completed reservations
          filtered = filtered.filter(res => {
            const reservationDate = parseISO(res.date);
            return isPast(reservationDate) && !isToday(reservationDate) &&
                   (res.checkInStatus === 'checked-out' || res.status === 'completed');
          });
          break;
        case 3: // Cancelled reservations
          filtered = filtered.filter(res => res.status === 'cancelled');
          break;
        case 4: // All reservations
          break;
      }
    }
    
    // Sort results based on current sort column and direction
    filtered = filtered.sort((a, b) => {
      let result = 0;
      
      switch (sortColumn) {
        case 'dateTime':
          // Combined date and time sorting
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          
          // First compare by date
          if (dateA.getTime() !== dateB.getTime()) {
            result = dateA.getTime() - dateB.getTime();
          } else {
            // If dates are equal, compare by start time
            const timeA = convertTimeToMinutes(a.startTime || '');
            const timeB = convertTimeToMinutes(b.startTime || '');
            result = timeA - timeB;
          }
          break;
          
        case 'customer':
          const nameA = `${a.customerDetails.firstName} ${a.customerDetails.lastName}`.toLowerCase();
          const nameB = `${b.customerDetails.firstName} ${b.customerDetails.lastName}`.toLowerCase();
          result = nameA.localeCompare(nameB);
          break;
          
        case 'vessel':
          const vesselA = (a.vesselType || '').toLowerCase();
          const vesselB = (b.vesselType || '').toLowerCase();
          result = vesselA.localeCompare(vesselB);
          break;
          
        case 'time':
          const startTimeA = convertTimeToMinutes(a.startTime || '');
          const startTimeB = convertTimeToMinutes(b.startTime || '');
          result = startTimeA - startTimeB;
          break;
          
        case 'status':
          const statusA = a.paymentStatus || 'unpaid';
          const statusB = b.paymentStatus || 'unpaid';
          result = statusA.localeCompare(statusB);
          break;
          
        case 'checkin':
          const checkinA = a.checkInStatus || '';
          const checkinB = b.checkInStatus || '';
          result = checkinA.localeCompare(checkinB);
          break;
          
        case 'createdAt':
          const getDateFromCreatedAt = (createdAt: any) => {
            if (!createdAt) return new Date(0);
            try {
              // Handle Firestore Timestamp objects
              if (createdAt && typeof createdAt.toDate === 'function') {
                return createdAt.toDate();
              } else if (createdAt && typeof createdAt === 'object' && createdAt.seconds) {
                // Handle Firestore Timestamp-like objects
                return new Date(createdAt.seconds * 1000);
              } else {
                // Handle regular date strings/objects
                return new Date(createdAt);
              }
            } catch (error) {
              return new Date(0);
            }
          };
          
          const createdAtA = getDateFromCreatedAt(a.createdAt);
          const createdAtB = getDateFromCreatedAt(b.createdAt);
          result = createdAtA.getTime() - createdAtB.getTime();
          break;
          
        default:
          // Default to date+time sorting
          const defaultDateA = new Date(a.date);
          const defaultDateB = new Date(b.date);
          
          if (defaultDateA.getTime() !== defaultDateB.getTime()) {
            result = defaultDateA.getTime() - defaultDateB.getTime();
          } else {
            const defaultTimeA = convertTimeToMinutes(a.startTime || '');
            const defaultTimeB = convertTimeToMinutes(b.startTime || '');
            result = defaultTimeA - defaultTimeB;
          }
          break;
      }
      
      // Apply sort direction
      return sortDirection === 'asc' ? result : -result;
    });
    
    return filtered;
  };
  
  const filteredReservations = getFilteredReservations();
  
  // Get today's reservations for quick stats (active only)
  const todayReservations = reservations.filter(res => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return res.date === today && 
           res.status !== 'cancelled' && 
           res.status !== 'completed' && 
           res.checkInStatus !== 'checked-out';
  });
  
  const todayCheckIns = todayReservations.filter(res => 
    res.checkInStatus === 'checked-in' || res.checkInStatus === 'checked-out'
  );
  
  const pendingReservations = reservations.filter(res => res.status === 'pending');
  
  // Get this week's reservations
  const thisWeekReservations = reservations.filter(res => {
    const reservationDate = parseISO(res.date);
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    return isWithinInterval(reservationDate, { start: weekStart, end: weekEnd });
  });
  
  // Render overview stats with improved responsive grid
  const renderOverviewStats = () => (
    <Grid container spacing={1} sx={{ mb: { xs: 2, sm: 3 } }}>
      <Grid item xs={6} sm={6} lg={6}>
        <Card
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            height: '100%',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            cursor: 'pointer',
            border: statCardFilter === 'today' ? '2px solid #1976d2' : 'none',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: { xs: 'none', sm: 'translateY(-4px)' },
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}
          onClick={() => handleStatCardClick('today')}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <CalendarTodayIcon sx={{ 
              color: 'primary.main', 
              mr: { xs: 0, sm: 1 },
              mb: { xs: 0.5, sm: 0 },
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }} />
            <Typography variant="h6" color="primary" sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.85rem', sm: '1rem', md: '1.25rem' },
              textAlign: 'center'
            }}>
              Today's Reservations
            </Typography>
          </Box>
          <Typography variant="h2" sx={{ 
            fontWeight: 700, 
            mb: { xs: 0.5, sm: 1 }, 
            color: 'primary.main', 
            textAlign: 'center',
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
          }}>
            {todayReservations.length}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
          }}>
            <CheckCircleIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, color: 'success.main' }} />
            {todayCheckIns.length} checked in
          </Typography>
        </Card>
      </Grid>
      
      <Grid item xs={6} sm={6} lg={6}>
        <Card
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            height: '100%',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            cursor: 'pointer',
            border: statCardFilter === 'week' ? '2px solid #2e7d32' : 'none',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: { xs: 'none', sm: 'translateY(-4px)' },
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}
          onClick={() => handleStatCardClick('week')}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <DateRangeIcon sx={{ 
              color: 'success.main', 
              mr: { xs: 0, sm: 1 },
              mb: { xs: 0.5, sm: 0 },
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }} />
            <Typography variant="h6" color="success.main" sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.85rem', sm: '1rem', md: '1.25rem' },
              textAlign: 'center'
            }}>
              This Week
            </Typography>
          </Box>
          <Typography variant="h2" sx={{ 
            fontWeight: 700, 
            mb: { xs: 0.5, sm: 1 }, 
            color: 'success.main', 
            textAlign: 'center',
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
          }}>
            {thisWeekReservations.length}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
          }}>
            <EventIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, color: 'success.main' }} />
            Total Reservations
          </Typography>
        </Card>
      </Grid>
    </Grid>
  );

  // Mobile card view for reservations
  const renderMobileReservationCard = (reservation: any, index: number) => {
    const reservationDate = parseISO(reservation.date);
    const isExpired = isPast(reservationDate) && !isToday(reservationDate);
    const isCancelled = reservation.status === 'cancelled';
    const isCheckedIn = reservation.checkInStatus === 'checked-in';
    const isCheckedOut = reservation.checkInStatus === 'checked-out';
    
    return (
      <Card
        key={reservation.id || `reservation-${index}`}
        sx={{
          mb: 1.5,
          cursor: 'pointer',
          borderLeft: isCancelled 
            ? '4px solid #f44336' 
            : isToday(reservationDate)
              ? '4px solid #ffeb3b'
              : '4px solid transparent',
          '&:hover': {
            boxShadow: 3,
          },
          width: '100%',
          maxWidth: '100%'
        }}
        onClick={() => reservation.id && navigate(`/staff/reservations/${reservation.id}`)}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <Typography variant="subtitle1" component="div" sx={{ mb: 0.5, fontWeight: 'medium', fontSize: '0.925rem' }} noWrap>
                {`${reservation.customerDetails.firstName} ${reservation.customerDetails.lastName}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {format(parseISO(reservation.date), 'MMM d, yyyy')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
              <Chip 
                label={reservation.checkInStatus || 'Not checked in'} 
                color={
                  reservation.checkInStatus === 'checked-in' ? 'primary' : 
                  reservation.checkInStatus === 'checked-out' ? 'success' : 
                  'default'
                }
                size="small"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
              {reservation.payment && reservation.payment.depositPaid && reservation.payment.remainingAmount > 0 ? (
                <Chip label="Deposit Paid" color="info" size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
              ) : reservation.payment && reservation.payment.remainingAmount <= 0 ? (
                <Chip label="Paid" color="success" size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
              ) : (
                <Chip label={reservation.paymentStatus || 'unpaid'} color="warning" size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
              )}
            </Box>
          </Box>
          
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
              <ScheduleIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                {formatTime12Hour(reservation.startTime)} - {formatTime12Hour(reservation.endTime)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
              <BoatIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                {reservation.vesselType ? toTitleCase(reservation.vesselType) : ''}
              </Typography>
            </Box>
          </Stack>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarTodayIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Booked: {(() => {
                if (!reservation.createdAt) return 'N/A';
                try {
                  let date;
                  // Handle Firestore Timestamp objects
                  if (reservation.createdAt && typeof reservation.createdAt.toDate === 'function') {
                    date = reservation.createdAt.toDate();
                  } else if (reservation.createdAt && typeof reservation.createdAt === 'object' && reservation.createdAt.seconds) {
                    // Handle Firestore Timestamp-like objects
                    date = new Date(reservation.createdAt.seconds * 1000);
                  } else {
                    // Handle regular date strings/objects
                    date = new Date(reservation.createdAt);
                  }
                  
                  if (isNaN(date.getTime())) return 'N/A';
                  return format(date, 'MM/dd/yy h:mm a');
                } catch (error) {
                  return 'N/A';
                }
              })()}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            {!isCancelled && (
              <>
                {!isExpired && !isCheckedOut && (
                  <Tooltip title="Cancel">
                    <IconButton
                      color="error"
                      size="small"
                      sx={{ padding: '4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelReservation(reservation);
                      }}
                    >
                      <CancelIcon fontSize="small" sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {isToday(reservationDate) && !isCheckedIn && !isCheckedOut && reservation.status === 'confirmed' && (
                  <Tooltip title="Check In">
                    <IconButton
                      color="primary"
                      size="small"
                      sx={{ padding: '4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCheckInReservation(reservation);
                        setCheckInDialogOpen(true);
                      }}
                    >
                      <CheckInIcon fontSize="small" sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {isCheckedIn && !isCheckedOut && (
                  <Tooltip title="Check Out">
                    <IconButton
                      color="success"
                      size="small"
                      sx={{ padding: '4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheckOut(reservation);
                      }}
                    >
                      <CheckOutIcon fontSize="small" sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            
            <Tooltip title="Details">
              <IconButton
                color="info"
                size="small"
                sx={{ padding: '4px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (reservation.id) {
                    navigate(`/staff/reservations/${reservation.id}`);
                  }
                }}
              >
                <InfoIcon fontSize="small" sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Table row renderer (for desktop/tablet view)
  const renderTableRow = (reservation: any, index: number) => {
    const reservationDate = parseISO(reservation.date);
    const isExpired = isPast(reservationDate) && !isToday(reservationDate);
    const isCancelled = reservation.status === 'cancelled';
    const isCheckedIn = reservation.checkInStatus === 'checked-in';
    const isCheckedOut = reservation.checkInStatus === 'checked-out';
    
    // Make sure we have a valid ID to use as key, or use the index as fallback
    const rowKey = reservation.id ? reservation.id : `reservation-${index}`;
    
    return (
      <TableRow 
        key={rowKey}
        hover
        onClick={() => reservation.id && navigate(`/staff/reservations/${reservation.id}`)}
        sx={{ 
          cursor: 'pointer',
          backgroundColor: isCancelled 
            ? 'rgba(244, 67, 54, 0.05)' 
            : isToday(reservationDate)
              ? 'rgba(255, 235, 59, 0.1)'
              : 'inherit',
          '&:hover': {
            backgroundColor: isCancelled 
              ? 'rgba(244, 67, 54, 0.1)' 
              : isToday(reservationDate)
                ? 'rgba(255, 235, 59, 0.2)'
                : 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <TableCell>
          {format(parseISO(reservation.date), 'MM/dd/yyyy')}
        </TableCell>
        <TableCell>
          {`${reservation.customerDetails.firstName} ${reservation.customerDetails.lastName}`}
        </TableCell>
        <TableCell>
          {reservation.vesselType ? toTitleCase(reservation.vesselType) : ''}
        </TableCell>
        <TableCell>
          {formatTime12Hour(reservation.startTime)} - {formatTime12Hour(reservation.endTime)}
        </TableCell>
        <TableCell>
          {reservation.payment && reservation.payment.depositPaid && reservation.payment.remainingAmount > 0 ? (
            <Chip label="Deposit Paid" color="info" size="small" />
          ) : reservation.payment && reservation.payment.remainingAmount <= 0 ? (
            <Chip label="Paid" color="success" size="small" />
          ) : (
            <Chip label={reservation.paymentStatus || 'unpaid'} color="warning" size="small" />
          )}
        </TableCell>
        <TableCell>
          {reservation.checkInStatus ? (
            <Chip 
              label={reservation.checkInStatus} 
              color={
                reservation.checkInStatus === 'checked-in' ? 'primary' : 
                reservation.checkInStatus === 'checked-out' ? 'success' : 
                'default'
              }
              size="small"
            />
          ) : (
            <Chip label="Not checked in" size="small" variant="outlined" />
          )}
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
          {(() => {
            if (!reservation.createdAt) return 'N/A';
            try {
              let date;
              // Handle Firestore Timestamp objects
              if (reservation.createdAt && typeof reservation.createdAt.toDate === 'function') {
                date = reservation.createdAt.toDate();
              } else if (reservation.createdAt && typeof reservation.createdAt === 'object' && reservation.createdAt.seconds) {
                // Handle Firestore Timestamp-like objects
                date = new Date(reservation.createdAt.seconds * 1000);
              } else {
                // Handle regular date strings/objects
                date = new Date(reservation.createdAt);
              }
              
              if (isNaN(date.getTime())) return 'N/A';
              return format(date, 'MM/dd/yy h:mm a');
            } catch (error) {
              return 'N/A';
            }
          })()}
        </TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          {!isCancelled && (
            <>
              {!isExpired && !isCheckedOut && (
                <Tooltip title="Cancel Reservation">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelReservation(reservation);
                    }}
                  >
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {isToday(reservationDate) && !isCheckedIn && !isCheckedOut && reservation.status === 'confirmed' && (
                <Tooltip title="Check In">
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCheckInReservation(reservation);
                      setCheckInDialogOpen(true);
                    }}
                  >
                    <CheckInIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {isCheckedIn && !isCheckedOut && (
                <Tooltip title="Check Out">
                  <IconButton
                    color="success"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCheckOut(reservation);
                    }}
                  >
                    <CheckOutIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {(reservation.status === 'confirmed' || reservation.status === 'completed') && 
               reservation.payment && reservation.payment.id && 
               !reservation.payment.refunded && (
                <Tooltip title="Process Refund">
                  <IconButton
                    color="warning"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefundReservation(reservation);
                    }}
                  >
                    <ReceiptIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          
          <Tooltip title="View Details">
            <IconButton
              color="info"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (reservation.id) {
                  navigate(`/staff/reservations/${reservation.id}`);
                }
              }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };
  
  // Handler for resending confirmation email
  const handleResendConfirmation = async () => {
    if (!detailReservation) return;
    
    // Set the default email to the customer's email
    setResendEmail(detailReservation.customerDetails.email);
    setOpenResendDialog(true);
  };

  const handleConfirmResend = async () => {
    try {
      setResendingEmail(true);
      console.log('Resending email for reservation:', detailReservation);
      
      // Get the email templates for this location
      const locationId = detailReservation.locationId || FALLEN_LEAF_LOCATION_ID;
      const templatesRef = collection(db, `locations/${locationId}/transactionalEmailTemplates`);
      const q = query(templatesRef, where('type', '==', 'reservation_confirmation'));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        throw new Error('No reservation confirmation templates found');
      }
      
      const templates = querySnapshot.docs.map(doc => doc.data());
      // Find default template
      const template = templates.find(t => t.isDefault) || templates[0];
      
      if (!template) {
        console.error('No default reservation_confirmation template found. Available templates:', templates);
        throw new Error('No default reservation confirmation template found');
      }
      
      console.log('Using template:', template);

      // Get location data directly from Firestore
      const locationDoc = await getDoc(doc(db, 'locations', locationId));
      
      if (!locationDoc.exists()) {
        throw new Error(`Location not found with ID: ${locationId}`);
      }
      
      const locationData = locationDoc.data();
      console.log('Location data for email:', locationData);

      // Now check and update the template content to use underscore notation instead of dot notation
      let updatedBody = template.body;
      
      // Replace problematic nested references with underscore notation
      updatedBody = updatedBody.replace(/{{customerDetails\.firstName}}/g, '{{customerDetails_firstName}}');
      updatedBody = updatedBody.replace(/{{payment\.depositAmount}}/g, '{{payment_depositAmount}}');
      updatedBody = updatedBody.replace(/{{payment\.remainingAmount}}/g, '{{payment_remainingAmount}}');
      
      // Prepare email content
      const subject = template.subject;
      const body = updatedBody; // Use the updated template
      
      console.log('Updated template format:', body);

      // Format vessel type to capitalize first letter of each word
      const formatVesselType = (type: string) => {
        return type.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      };

      // Format currency value
      const formatCurrency = (value?: number) => {
        if (value === undefined) return '';
        return value.toFixed(2);
      };

      // Prepare all variables to be passed to the email function
      const variables = {
        id: detailReservation.id,
        customerDetails_firstName: detailReservation.customerDetails.firstName,
        customerDetails_lastName: detailReservation.customerDetails.lastName,
        customerDetails_email: detailReservation.customerDetails.email,
        customerDetails_phone: formatPhoneNumber(detailReservation.customerDetails.phone),
        vesselType: formatVesselType(detailReservation.vesselType),
        vesselName: detailReservation.vesselName,
        date: format(parseISO(detailReservation.date), 'MMMM d, yyyy'),
        startDate: formatInTimeZone(detailReservation.date, 'America/Los_Angeles', 'M/d/yyyy'),
        startTime: formatTime12Hour(detailReservation.startTime),
        endTime: formatTime12Hour(detailReservation.endTime),
        partySize: detailReservation.partySize,
        originalPrice: formatCurrency(detailReservation.originalPrice || detailReservation.rentalPrice),
        rentalPrice: formatCurrency(detailReservation.rentalPrice),
        damageWaiverAmount: formatCurrency(detailReservation.damageWaiverAmount),
        taxAmount: formatCurrency(detailReservation.taxAmount),
        finalPrice: formatCurrency(detailReservation.finalPrice),
        discountAmount: formatCurrency(detailReservation.discountAmount),
        status: detailReservation.status,
        promoCode: detailReservation.promoCode || '',
        marinaName: locationData.name,
        marinaPhone: formatPhoneNumber(locationData.phone),
        marinaEmail: locationData.email,
        marinaAddress: locationData.address,
        marinaWebsite: locationData.website,

        // Flattened payment fields to ensure they're available at top level
        payment_depositAmount: Number(detailReservation.payment?.depositAmount || 0).toFixed(2),
        payment_remainingAmount: Number(detailReservation.payment?.remainingAmount || 0).toFixed(2),
        
        // Payment details
        paymentDetails: {
          cardBrand: detailReservation.paymentDetails?.cardBrand || '',
          last4: detailReservation.paymentDetails?.last4 || '',
        }
      };

      console.log('Sending email with variables:', variables);

      // Call Firebase Function to send email with all variables
      const sendEmailFunction = httpsCallable(functions, 'sendEmail');
      await sendEmailFunction({
        to: resendEmail,
        subject,
        body,
        templateId: template.id,
        reservationId: detailReservation.id, // Add reservationId for tracking
        ...variables  // Pass all variables to the function
      });

      setSnackbarMessage('Confirmation email resent successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenResendDialog(false);
      
      // Refresh correspondence data with a slight delay to ensure Firebase has time to update
      setTimeout(() => {
        fetchCorrespondenceData(detailReservation.id);
      }, 1500);
    } catch (error) {
      console.error('Error resending confirmation email:', error);
      setSnackbarMessage('Failed to resend confirmation email');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setResendingEmail(false);
    }
  };
  
  // Format date function for correspondence
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return format(date, 'MMM d, yyyy h:mm a');
  };
  
  // Function to handle viewing full email content
  const handleViewFullEmail = (email: EmailCorrespondence) => {
    setSelectedEmail(email);
    setEmailDetailDialogOpen(true);
  };
  
  const handleStatCardClick = (filter: string) => {
    if (statCardFilter !== filter) {
      setStatCardFilter(filter);
      setTimeout(() => {
        if (resultsTableRef.current) {
          resultsTableRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100); // Wait for render
    } else {
      setStatCardFilter(null);
    }
  };
  
  // Handle check-in mode toggle
  const handleCheckInModeToggle = () => {
    if (isCheckInMode) {
      setIsCheckInMode(false);
    } else {
      navigate('/staff/checkin');
    }
  };
  
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Responsive header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 4,
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          mb: 0,
          fontSize: { xs: '1.5rem', sm: '2rem' }
        }}>
          Reservations
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1
        }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCheckInModeToggle}
            startIcon={<CheckCircleIcon />}
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            {isCheckInMode ? 'Exit Check-in Mode' : 'Enter Check-in Mode'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetFilters}
            startIcon={<RefreshIcon />}
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            Reset Filters
          </Button>
        </Box>
      </Box>

      {renderOverviewStats()}

      {/* Responsive tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 1.5, sm: 2 }, maxWidth: '100%', overflowX: 'auto' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="reservation tabs"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            minHeight: { xs: 42, sm: 48 },
            '& .MuiTab-root': {
              minHeight: { xs: 42, sm: 48 },
              minWidth: { xs: 'auto', sm: 100 },
              padding: { xs: '4px 8px', sm: '6px 16px' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }
          }}
        >
          <Tab 
            label={isMobile ? "Today" : "Today"} 
            icon={<CheckIcon />} 
            iconPosition={isMobile ? "top" : "start"} 
            {...a11yProps(0)} 
          />
          <Tab 
            label={isMobile ? "Upcoming" : "Upcoming"} 
            icon={<ArrowForwardIcon />} 
            iconPosition={isMobile ? "top" : "start"} 
            {...a11yProps(1)} 
          />
          <Tab 
            label={isMobile ? "Done" : "Completed"} 
            icon={<CheckIcon />} 
            iconPosition={isMobile ? "top" : "start"} 
            {...a11yProps(2)} 
          />
          <Tab 
            label={isMobile ? "Cancelled" : "Cancelled"} 
            icon={<CancelIcon />} 
            iconPosition={isMobile ? "top" : "start"} 
            {...a11yProps(3)} 
          />
          <Tab 
            label={isMobile ? "All" : "All Reservations"} 
            icon={<FilterIcon />} 
            iconPosition={isMobile ? "top" : "start"} 
            {...a11yProps(4)} 
          />
        </Tabs>
      </Box>
      
      {/* Responsive filter layout */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2 }}
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />
              </InputAdornment>
            ),
            style: { fontSize: isMobile ? '0.875rem' : 'inherit' }
          }}
          sx={{ 
            flex: { xs: 1, sm: 2 },
            minWidth: { xs: 'auto', sm: 200 },
            maxWidth: { xs: 'none', sm: 400 },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        />
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, width: '100%' }}>
          <FormControl size="small" sx={{ 
            minWidth: { xs: '45%', sm: 150 },
            flex: { xs: 1, sm: 'unset' }
          }}>
            <InputLabel id="status-filter-label" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
              sx={{ fontSize: { xs: '0.875rem', sm: 'inherit' } }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label="Date"
            type="date"
            size="small"
            value={dateFilter ? format(dateFilter, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value) : null;
              handleDateFilterChange(date);
            }}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              style: { fontSize: isMobile ? '0.875rem' : 'inherit' }
            }}
            sx={{ 
              minWidth: { xs: '55%', sm: 200 },
              flex: { xs: 1, sm: 'unset' },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
          />
        </Box>
      </Stack>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      ) : filteredReservations.length > 0 ? (
        <>
          {/* Mobile view - Card layout */}
          {isMobile ? (
            <Box sx={{ width: '100%', maxWidth: '100vw', pr: 1 }}>
              {filteredReservations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((reservation, index) => renderMobileReservationCard(reservation, index))}
            </Box>
          ) : (
            /* Desktop/Tablet view - Table layout */
            <TableContainer ref={resultsTableRef} sx={{ mb: 2 }}>
              <Table sx={{ minWidth: { xs: 300, sm: 700 } }}>
                <TableHead>
                  <TableRow>
                    <SortableTableCell
                      sortKey="dateTime"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Date
                    </SortableTableCell>
                    <SortableTableCell
                      sortKey="customer"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Customer
                    </SortableTableCell>
                    <SortableTableCell
                      sortKey="vessel"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                    >
                      Vessel
                    </SortableTableCell>
                    <SortableTableCell
                      sortKey="time"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      Time
                    </SortableTableCell>
                    <SortableTableCell
                      sortKey="status"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      sx={{ display: { xs: 'none', md: 'table-cell' } }}
                    >
                      Status
                    </SortableTableCell>
                    <SortableTableCell
                      sortKey="checkin"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      sx={{ display: { xs: 'none', md: 'table-cell' } }}
                    >
                      Check-in
                    </SortableTableCell>
                    <SortableTableCell
                      sortKey="createdAt"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      sx={{ display: { xs: 'none', lg: 'table-cell' } }}
                    >
                      Booked
                    </SortableTableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReservations
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((reservation, index) => renderTableRow(reservation, index))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <TablePagination
            rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25, 50]}
            component="div"
            count={filteredReservations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={isMobile ? "Rows:" : "Rows per page:"}
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0.5, sm: 0 },
                padding: { xs: '8px', sm: '16px 0' },
                minHeight: { xs: 'unset', sm: '52px' }
              },
              '& .MuiTablePagination-selectLabel': {
                margin: { xs: 0, sm: undefined },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              },
              '& .MuiTablePagination-select': {
                marginLeft: { xs: '4px', sm: '8px' },
                marginRight: { xs: '4px', sm: '8px' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              },
              '& .MuiTablePagination-displayedRows': {
                margin: { xs: '8px 0', sm: 0 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              },
              '& .MuiTablePagination-actions': {
                marginLeft: { xs: 0, sm: '8px' },
                display: 'flex',
                gap: { xs: '4px', sm: '8px' }
              },
              '& .MuiButtonBase-root': {
                padding: { xs: '4px', sm: '8px' }
              }
            }}
          />
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            No Reservations Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tabValue === 0 
              ? "There are no reservations scheduled for today." 
              : "No reservations match your current filter criteria."}
          </Typography>
        </Box>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 2 
      }}>
        <Button 
          variant="contained" 
          color="primary" 
          component={Link} 
          to="/staff/new"
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
          sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}
        >
          Create New Reservation
        </Button>
      </Box>

      {/* Cancel Reservation Dialog */}
      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Cancel Reservation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to cancel this reservation for {selectedReservation?.customerDetails?.firstName} {selectedReservation?.customerDetails?.lastName}?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Cancellation Reason"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            error={!!cancelReasonError}
            helperText={cancelReasonError}
            required
          />
          {showRefundOption && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This reservation has a payment that may need to be refunded. You will be prompted to process a refund after cancellation.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          <Button onClick={() => setOpenCancelDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmCancel} 
            color="error" 
            variant="contained"
            fullWidth={isMobile}
          >
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog with responsive improvements */}
      <Dialog
        open={openRefundDialog}
        onClose={() => setOpenRefundDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Process a refund for {selectedReservation?.customerDetails?.firstName} {selectedReservation?.customerDetails?.lastName}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Refund Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Refund Reason"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          <Button onClick={() => setOpenRefundDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmRefund} 
            color="primary" 
            variant="contained"
            fullWidth={isMobile}
          >
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check Out Dialog with responsive improvements */}
      <Dialog
        open={openCheckOutDialog}
        onClose={() => setOpenCheckOutDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Check Out Reservation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Check out {selectedReservation?.customerDetails?.firstName} {selectedReservation?.customerDetails?.lastName}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Check-out Notes (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={checkOutNotes}
            onChange={(e) => setCheckOutNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          <Button onClick={() => setOpenCheckOutDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmCheckOut} 
            color="success" 
            variant="contained"
            fullWidth={isMobile}
          >
            Confirm Check Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check-in Dialog */}
      <CheckInDialog
        open={checkInDialogOpen}
        onClose={() => {
          setCheckInDialogOpen(false);
          // Refresh reservations to get updated data
          const fetchReservations = async () => {
            try {
              const data = await getAllReservationsWithCustomers();
              setReservations(data);
              setSnackbarMessage('Reservation checked in successfully');
              setSnackbarSeverity('success');
              setSnackbarOpen(true);
            } catch (err) {
              console.error('Error refreshing reservations:', err);
            }
          };
          fetchReservations();
        }}
        reservation={checkInReservation}
      />

      {/* Detail Dialog with responsive improvements */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" component="div">
              Reservation Details
            </Typography>
            {detailReservation && (
              <Typography variant="body2" color="text.secondary">
                #{detailReservation.id}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailReservation ? (
            <>
              <Tabs 
                value={detailTabValue} 
                onChange={handleDetailTabChange}
                variant={isMobile ? "fullWidth" : "standard"}
              >
                <Tab label="Details" />
                <Tab label="Email History" />
              </Tabs>
              
              <TabPanel value={detailTabValue} index={0}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>Customer Information</Typography>
                    <Typography variant="body2">
                      {detailReservation.customerDetails.firstName} {detailReservation.customerDetails.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {detailReservation.customerDetails.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatPhoneNumber(detailReservation.customerDetails.phone)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>Reservation Details</Typography>
                    <Typography variant="body2">
                      Date: {format(parseISO(detailReservation.date), 'MMM d, yyyy')}
                    </Typography>
                    <Typography variant="body2">
                      Time: {formatTime12Hour(detailReservation.startTime)} - {formatTime12Hour(detailReservation.endTime)}
                    </Typography>
                    <Typography variant="body2">
                      Vessel: {detailReservation.vesselType} - {detailReservation.vesselName}
                    </Typography>
                    <Typography variant="body2">
                      Party Size: {detailReservation.partySize}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Payment Information</Typography>
                    <Typography variant="body2">
                      Total: ${detailReservation.finalPrice?.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      Status: {detailReservation.paymentStatus || 'Unpaid'}
                    </Typography>
                  </Grid>
                </Grid>
              </TabPanel>
              
              <TabPanel value={detailTabValue} index={1}>
                {correspondenceLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : correspondence.length > 0 ? (
                  <List>
                    {correspondence.map((email, index) => (
                      <React.Fragment key={index}>
                        <Box sx={{ p: 2 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'center' }, 
                            mb: 1,
                            gap: { xs: 0.5, sm: 0 }
                          }}>
                            <Typography variant="subtitle2">{email.subject}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(email.timestamp)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            To: {email.recipient}
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => handleViewFullEmail(email)}
                            startIcon={<EmailIcon />}
                          >
                            View Full Email
                          </Button>
                        </Box>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No email correspondence found for this reservation.
                  </Typography>
                )}
              </TabPanel>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          {detailReservation && (
            <Button
              onClick={handleResendConfirmation}
              startIcon={<EmailIcon />}
              disabled={resendingEmail}
              fullWidth={isMobile}
            >
              {resendingEmail ? 'Sending...' : 'Resend Confirmation'}
            </Button>
          )}
          <Button onClick={() => setDetailDialogOpen(false)} fullWidth={isMobile}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Resend Dialog with responsive improvements */}
      <Dialog
        open={openResendDialog}
        onClose={() => setOpenResendDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Resend Confirmation Email</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the email address to send the confirmation to:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          <Button onClick={() => setOpenResendDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmResend} 
            color="primary" 
            variant="contained"
            disabled={resendingEmail}
            fullWidth={isMobile}
          >
            {resendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Detail Dialog with responsive improvements */}
      <Dialog
        open={emailDetailDialogOpen}
        onClose={() => setEmailDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Email Details</DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <>
              <Typography variant="subtitle2" gutterBottom>Subject:</Typography>
              <Typography variant="body2" gutterBottom>{selectedEmail.subject}</Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>To:</Typography>
              <Typography variant="body2" gutterBottom>{selectedEmail.recipient}</Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Sent:</Typography>
              <Typography variant="body2" gutterBottom>{formatDate(selectedEmail.timestamp)}</Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Content:</Typography>
              <MuiPaper sx={{ 
                p: 2, 
                backgroundColor: 'grey.50',
                maxHeight: { xs: '300px', sm: '400px' },
                overflow: 'auto'
              }}>
                <div dangerouslySetInnerHTML={{ __html: selectedEmail.body }} />
              </MuiPaper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDetailDialogOpen(false)} fullWidth={isMobile}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ 
          vertical: 'bottom', 
          horizontal: isMobile ? 'center' : 'right' 
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffReservationsPage; 